// CustomLocationForm — iter67 rebuild.
//
// Create mode (/my-places/custom/new): minimal 3-field form (name, description,
// whatsapp) with auto-GPS capture in the background. After save, swaps to a
// full-screen photo prompt with three nav options (Back to Discover, Request
// a Ride, Share this Location).
//
// Edit mode (/my-places/custom/:id/edit): keeps the previous full form with
// all fields (category, phone, address, photos, hours, smoking policy, etc).
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, X, Camera, MapPin, Loader2, Gem, MapPinPlus, Image as ImageIcon,
  Compass, Car, Home as HomeIcon, Save,
} from "lucide-react";
import { toast } from "sonner";
import { api, getMe } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useGps } from "../lib/useGps";
import * as guestStore from "../lib/guestStorage";
import InteractiveCrop from "../components/InteractiveCrop";

export default function CustomLocationForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  if (isEdit) return <CustomLocationEditForm id={id} />;
  return <CustomLocationCreateForm />;
}

/* ====================================================================== */
/*  CREATE — minimal 3-field flow with auto-GPS + post-save photo prompt   */
/* ====================================================================== */
function CustomLocationCreateForm() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const { coords: gpsCoords, state: gpsState, requestGps } = useGps();

  // GPS status driven by useGps + local override after retry.
  // override = null | "capturing" | "ok" | "failed"
  const [override, setOverride] = useState(null);
  const [overrideCoords, setOverrideCoords] = useState(null);

  const coords = overrideCoords || gpsCoords;
  let gpsStatus = "capturing";
  if (override) gpsStatus = override;
  else if (coords) gpsStatus = "ok";
  else if (gpsState === "granted") gpsStatus = "capturing";
  else gpsStatus = "failed"; // unknown / denied — let user tap to retry

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [busy, setBusy] = useState(false);

  // Post-save state
  const [createdDoc, setCreatedDoc] = useState(null); // server response

  const nameInputRef = useRef(null);

  // Auto-focus name field on mount (opens keyboard on mobile)
  useEffect(() => {
    const t = setTimeout(() => nameInputRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, []);

  // Silently start GPS capture in the background — only if browser permission
  // is already granted. If state is "unknown" or "denied", we DO NOT trigger
  // the primer here (it would block the form). Instead status shows "failed"
  // and the user can tap to retry, which routes through the primer.
  useEffect(() => {
    if (gpsCoords || gpsState !== "granted") return;
    let cancelled = false;
    (async () => {
      try {
        await requestGps("save-location-silent");
        if (cancelled) return;
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [gpsCoords, gpsState, requestGps]);

  const retryGps = async () => {
    setOverride("capturing");
    try {
      const res = await requestGps("save-location-retry");
      if (res.ok) {
        setOverrideCoords({ lat: res.lat, lng: res.lng });
        setOverride("ok");
      } else {
        setOverride("failed");
      }
    } catch {
      setOverride("failed");
    }
  };

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const body = {
        name: trimmed,
        description: description.trim() || "",
        whatsapp: whatsapp.trim() || "",
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
      };
      // iter106 — Guest-aware save. Guests persist to localStorage via
      // guestStore (later migrated to the account on first login). Authed
      // users hit the API. Either path returns the same shape so the photo
      // prompt below works for both.
      const me = await getMe();
      let saved;
      if (me) {
        const { data } = await api.post("/custom-locations", body);
        saved = data;
      } else {
        saved = guestStore.addCustomLocation(body);
      }
      toast.success(lang === "es" ? "Ubicación guardada" : "Location saved");
      setCreatedDoc(saved);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  if (createdDoc) {
    return <PhotoPrompt doc={createdDoc} t={t} lang={lang} />;
  }

  const canSave = name.trim().length > 0 && !busy;

  return (
    <main data-testid="custom-location-form" className="min-h-screen pb-32" style={{ background: "#0B0B0B" }}>
      {/* Header */}
      <header
        className="px-5 pt-4 pb-3 flex items-center gap-3 sticky top-0 z-10"
        style={{ background: "rgba(11,11,11,0.92)", backdropFilter: "blur(8px)" }}
      >
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Back"
          data-testid="sal-back"
          className="grid place-items-center rounded-full hover:bg-white/[0.05]"
          style={{ width: 44, height: 44, minHeight: 48, minWidth: 48, color: "#F5F1E8" }}
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
        </button>
        <h1
          className="flex-1 text-foreground"
          style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, lineHeight: 1.2 }}
        >
          {t("cl_form_new_title")}
        </h1>
        <button
          type="button"
          onClick={() => navigate("/journey")}
          aria-label="Close"
          data-testid="sal-close"
          className="grid place-items-center rounded-full hover:bg-white/[0.05]"
          style={{ width: 32, height: 32, color: "#B0AAA3" }}
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </header>

      {/* GPS status line */}
      <div className="px-5 mt-4">
        <GpsStatusLine status={gpsStatus} t={t} onRetry={retryGps} />
      </div>

      {/* Local gem badge */}
      <div className="px-5 mt-4">
        <span
          className="inline-flex items-center gap-1 px-2.5 py-1"
          style={{ border: "1px solid rgba(232,184,75,0.4)", color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase" }}
        >
          <Gem className="w-3 h-3" strokeWidth={1.5} /> Local Gem
        </span>
      </div>

      {/* Fields */}
      <section className="px-5 mt-6 space-y-5">
        {/* Name — required */}
        <div>
          <FieldLabel required gold>
            {t("sal_name_label")} <span style={{ color: "#E8B84B", marginLeft: 4 }}>*</span>
          </FieldLabel>
          <input
            ref={nameInputRef}
            data-testid="sal-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("sal_name_placeholder")}
            autoComplete="off"
            autoCapitalize="words"
            style={{
              minHeight: 56,
              width: "100%",
              background: "#141414",
              border: "1px solid rgba(232,184,75,0.5)",
              borderRadius: 14,
              padding: "0 16px",
              fontSize: 16,
              color: "#F5F1E8",
              fontFamily: "'DM Sans', sans-serif",
            }}
          />
        </div>

        {/* Description — optional */}
        <div>
          <FieldLabel>{t("sal_desc_label")}</FieldLabel>
          <input
            data-testid="sal-desc-input"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("sal_desc_placeholder")}
            style={{
              minHeight: 46,
              width: "100%",
              background: "#141414",
              border: "0.5px solid #222",
              borderRadius: 12,
              padding: "0 14px",
              fontSize: 16,
              color: "#F5F1E8",
              fontFamily: "'DM Sans', sans-serif",
            }}
          />
        </div>

        {/* WhatsApp — optional */}
        <div>
          <FieldLabel>{t("sal_whatsapp_label")}</FieldLabel>
          <div className="relative">
            <span
              aria-hidden
              className="absolute top-1/2 -translate-y-1/2 left-3 grid place-items-center rounded-full"
              style={{ width: 22, height: 22, background: "#25D366", color: "#fff", fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 11 }}
            >
              W
            </span>
            <input
              data-testid="sal-whatsapp-input"
              type="tel"
              inputMode="tel"
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              placeholder={t("sal_whatsapp_placeholder")}
              style={{
                minHeight: 46,
                width: "100%",
                background: "#141414",
                border: "1px solid rgba(37,211,102,0.25)",
                borderRadius: 12,
                padding: "0 14px 0 38px",
                fontSize: 16,
                color: "#F5F1E8",
                fontFamily: "'DM Sans', sans-serif",
              }}
            />
          </div>
          <p className="mt-1.5" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#9A9490" }}>
            {t("sal_whatsapp_hint")}
          </p>
        </div>
      </section>

      {/* Save */}
      <div className="px-5 mt-9">
        <button
          type="button"
          data-testid="sal-save-btn"
          onClick={submit}
          disabled={!canSave}
          className="w-full inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-40"
          style={{
            minHeight: 52,
            background: "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)",
            color: "#0B0B0B",
            borderRadius: 14,
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            border: "none",
          }}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <MapPinPlus className="w-4 h-4" strokeWidth={1.5} />}
          {t("sal_save_btn")}
        </button>
        <p className="mt-3 text-center" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#9A9490" }}>
          {t("sal_only_required")}
        </p>
      </div>
    </main>
  );
}

/* ---------------- GPS status line ---------------- */
function GpsStatusLine({ status, t, onRetry }) {
  const isCapturing = status === "capturing";
  const isOk = status === "ok";
  const dotColor = isOk ? "#22c55e" : isCapturing ? "#E8B84B" : "#f59e0b";
  const text = isOk ? `${t("sal_gps_captured")} ✓` : isCapturing ? t("sal_gps_capturing") : t("sal_gps_tap");
  const textColor = isOk ? "#22c55e" : isCapturing ? "#E8B84B" : "#f59e0b";

  const inner = (
    <span className="inline-flex items-center gap-2" data-testid={`sal-gps-${status}`}>
      <span
        className={isCapturing ? "animate-pulse" : ""}
        style={{ width: 8, height: 8, borderRadius: 999, background: dotColor, boxShadow: `0 0 8px ${dotColor}` }}
      />
      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: textColor, letterSpacing: "0.04em" }}>
        {text}
      </span>
    </span>
  );

  if (status === "failed") {
    return (
      <button type="button" onClick={onRetry} className="hover:opacity-80 transition-opacity" style={{ minHeight: 32 }}>
        {inner}
      </button>
    );
  }
  return <div style={{ minHeight: 32 }} className="flex items-center">{inner}</div>;
}

/* ---------------- Photo prompt (post-save full screen) ---------------- */
function PhotoPrompt({ doc, t, lang }) {
  const navigate = useNavigate();
  const [photoDataUrl, setPhotoDataUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const cropRef = useRef(null);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      // iter130 — Hold the picked photo in local state so the user can
      // pan/zoom in the InteractiveCrop frame. The actual save now waits
      // for the explicit "Guardar Ubicación" tap.
      const dataUrl = await compressImage(file);
      setPhotoDataUrl(dataUrl);
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === "es" ? "No se pudo agregar la foto" : "Could not add photo"));
    }
  };

  const saveCropped = async () => {
    if (!photoDataUrl || busy) return;
    setBusy(true);
    try {
      const cropped = cropRef.current?.getCroppedDataURL?.() || photoDataUrl;
      const me = await getMe();
      if (me) {
        await api.patch(`/custom-locations/${doc.id}`, { photos: [{ url: cropped }] });
      } else {
        const list = guestStore.getCustomLocations().map((c) =>
          c.id === doc.id ? { ...c, photos: [{ url: cropped }] } : c,
        );
        try { localStorage.setItem("habano.custom_locations", JSON.stringify(list)); } catch { /* ignore */ }
      }
      toast.success(lang === "es" ? "Foto agregada" : "Photo added");
      // After save, drop back to discover so the user lands on the home
      // grid where their new spot is now indexed.
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === "es" ? "No se pudo agregar la foto" : "Could not add photo"));
    } finally {
      setBusy(false);
    }
  };

  const shareLink = `${window.location.origin}/places/custom/${doc.id}?t=${doc.share_token || ""}`;
  const shareMsg = t("sal_share_msg").replace("{link}", shareLink);
  const waUrl = `https://wa.me/?text=${encodeURIComponent(shareMsg)}`;

  return (
    <main data-testid="sal-photo-prompt" className="min-h-screen flex flex-col pb-12" style={{ background: "#0B0B0B" }}>
      <header className="px-5 pt-4 pb-3 flex items-center justify-end">
        <button
          type="button"
          onClick={() => navigate("/")}
          aria-label="Close"
          data-testid="sal-photo-close"
          className="grid place-items-center rounded-full hover:bg-white/[0.05]"
          style={{ width: 32, height: 32, color: "#B0AAA3" }}
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </header>

      <section className="flex-1 px-6 flex flex-col">
        {/* Icon */}
        <div className="grid place-items-center mt-4">
          <div
            className="rounded-full grid place-items-center"
            style={{
              width: 80, height: 80,
              background: "rgba(232,184,75,0.10)",
              border: "1px solid rgba(232,184,75,0.5)",
            }}
          >
            <Camera className="text-gold" style={{ width: 34, height: 34 }} strokeWidth={1.5} />
          </div>
        </div>

        {/* Title + sub */}
        <div className="text-center mt-5">
          <h1
            data-testid="sal-photo-title"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: "#F5F1E8", lineHeight: 1.2 }}
          >
            {t("sal_photo_title")}
          </h1>
          <p
            className="mt-2 mx-auto max-w-[300px]"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#B0AAA3", lineHeight: 1.5 }}
          >
            {t("sal_photo_sub")}
          </p>
        </div>

        {/* iter130 — Interactive 200px crop frame. Falls back to a dashed
            drop zone before a photo is picked. */}
        <div className="mt-6">
          {photoDataUrl ? (
            <InteractiveCrop
              src={photoDataUrl}
              cropRef={cropRef}
              frameHeight={200}
              testid="sal-photo-crop"
            />
          ) : (
            <label
              className="block cursor-pointer relative overflow-hidden"
              style={{
                width: "100%",
                height: 200,
                border: "1.5px dashed rgba(232,184,75,0.45)",
                borderRadius: 12,
                background: "#141414",
              }}
            >
              <div className="w-full h-full grid place-items-center" data-testid="sal-photo-dropzone">
                <ImageIcon className="text-foreground/40" style={{ width: 32, height: 32 }} strokeWidth={1.5} />
              </div>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="sal-photo-input"
                onChange={onPick}
              />
            </label>
          )}
        </div>

        {/* Add photo / change photo CTA */}
        <label
          className="mt-3 cursor-pointer inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
          style={{
            minHeight: 48,
            background: photoDataUrl ? "transparent" : "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)",
            color: photoDataUrl ? "#E8B84B" : "#0B0B0B",
            border: photoDataUrl ? "1px solid rgba(232,184,75,0.5)" : "none",
            borderRadius: 12,
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          <Camera className="w-4 h-4" strokeWidth={1.5} />
          {t("sal_photo_btn")}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="sal-photo-input-cta"
            onChange={onPick}
          />
        </label>

        {/* iter130 — Explicit "Save Location" only visible when a photo is
            queued for cropping. Tapping captures the visible region via
            the canvas API and persists it to the location. */}
        {photoDataUrl && (
          <button
            type="button"
            onClick={saveCropped}
            disabled={busy}
            data-testid="sal-photo-save-crop"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
            style={{
              minHeight: 48,
              background: "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)",
              color: "#0B0B0B",
              borderRadius: 12,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              border: "none",
            }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Save className="w-4 h-4" strokeWidth={1.5} />}
            {t("sal_photo_save_btn")}
          </button>
        )}

        {/* 3 navigation options + iter130 Back to Home */}
        <div className="mt-8 space-y-3">
          <button
            type="button"
            data-testid="sal-nav-discover"
            onClick={() => navigate("/")}
            className="w-full inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
            style={{
              minHeight: 48,
              background: "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)",
              color: "#0B0B0B",
              borderRadius: 12,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              border: "none",
            }}
          >
            <Compass className="w-4 h-4" strokeWidth={1.5} />
            {t("sal_back_discover")}
          </button>

          <button
            type="button"
            data-testid="sal-nav-ride"
            onClick={() => navigate("/airport")}
            className="w-full inline-flex items-center justify-center gap-2 hover:bg-[rgba(232,184,75,0.06)] transition-colors"
            style={{
              minHeight: 48,
              background: "transparent",
              color: "#E8B84B",
              border: "1px solid rgba(232,184,75,0.5)",
              borderRadius: 12,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              fontWeight: 500,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            <Car className="w-4 h-4" strokeWidth={1.5} />
            {t("sal_request_ride")}
          </button>

          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sal-nav-share"
            className="block text-center hover:opacity-80 transition-opacity"
            style={{
              minHeight: 44,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              color: "#25D366",
              paddingTop: 14,
            }}
          >
            <WaIcon /> {t("sal_share_location")}
          </a>

          {/* iter130 — Back to Home (gold outline, full width, 48px, home icon). */}
          <button
            type="button"
            data-testid="sal-nav-home"
            onClick={() => navigate("/")}
            className="w-full inline-flex items-center justify-center gap-2 hover:bg-[rgba(232,184,75,0.06)] transition-colors"
            style={{
              minHeight: 48,
              background: "transparent",
              color: "#E8B84B",
              border: "1px solid rgba(232,184,75,0.5)",
              borderRadius: 12,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              fontWeight: 500,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            <HomeIcon className="w-4 h-4" strokeWidth={1.5} />
            {t("sal_back_home")}
          </button>
        </div>
      </section>
    </main>
  );
}

function WaIcon() {
  return (
    <svg className="inline-block mr-1.5 -mt-0.5" width="14" height="14" viewBox="0 0 24 24" fill="#25D366" aria-hidden>
      <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24z" />
    </svg>
  );
}

function FieldLabel({ children, gold }) {
  return (
    <div
      className="mb-2"
      style={{
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 11,
        color: gold ? "#E8B84B" : "#B0AAA3",
        letterSpacing: "0.18em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </div>
  );
}

async function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1600;
        const ratio = Math.min(MAX / img.width, MAX / img.height, 1);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * ratio);
        canvas.height = Math.round(img.height * ratio);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = () => reject(new Error("img error"));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

/* ====================================================================== */
/*  EDIT — full form with all fields (reached via the upgrade card)        */
/* ====================================================================== */
function CustomLocationEditForm({ id }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { requestGps } = useGps();

  const [form, setForm] = useState({
    name: "", description: "", address: "", lat: "", lng: "",
    phone: "", whatsapp: "", instagram: "", website: "", hours: "",
    smoking_policy: "", notes: "",
    request_directory: false,
  });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // iter106 — Guest-saved locations live only in localStorage. Their ids
    // start with "g_" (see guestStorage.uid()). Read them locally so the
    // edit form works without an API session.
    if (id && id.startsWith("g_")) {
      const local = guestStore.getCustomLocations().find((c) => c.id === id);
      if (local) {
        setForm({
          name: local.name || "", description: local.description || "",
          address: local.address || "", lat: local.lat ?? "", lng: local.lng ?? "",
          phone: local.phone || "", whatsapp: local.whatsapp || "",
          instagram: local.instagram || "", website: local.website || "",
          hours: local.hours || "", smoking_policy: local.smoking_policy || "",
          notes: local.notes || "",
          request_directory: false,
        });
        setPhotos((local.photos || []).map((p) => ({ url: p.url })));
      } else {
        toast.error("Could not load location");
        navigate("/journey");
      }
      setLoading(false);
      return;
    }
    api.get(`/custom-locations/${id}`)
      .then(({ data }) => {
        setForm({
          name: data.name || "", description: data.description || "",
          address: data.address || "", lat: data.lat || "", lng: data.lng || "",
          phone: data.phone || "", whatsapp: data.whatsapp || "",
          instagram: data.instagram || "", website: data.website || "",
          hours: data.hours || "", smoking_policy: data.smoking_policy || "",
          notes: data.notes || "",
          request_directory: data.directory_request_status === "pending",
        });
        setPhotos((data.photos || []).map((p) => ({ url: p.url })));
      })
      .catch(() => { toast.error("Could not load location"); navigate("/journey"); })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const captureGPS = async () => {
    const res = await requestGps("distance");
    if (res.ok) {
      setForm((p) => ({ ...p, lat: res.lat.toFixed(6), lng: res.lng.toFixed(6) }));
      toast.success("GPS captured");
    } else if (res.reason !== "soft-denied") {
      toast.message(t("map_location_unavailable"));
    }
  };

  const onPhotoPick = async (e) => {
    const files = Array.from(e.target.files || []);
    const next = [];
    for (const f of files) {
      const dataUrl = await compressImage(f);
      next.push({ url: dataUrl });
    }
    setPhotos((prev) => [...prev, ...next]);
    e.target.value = "";
  };

  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setBusy(true);
    const body = {
      ...form,
      lat: form.lat === "" ? null : Number(form.lat),
      lng: form.lng === "" ? null : Number(form.lng),
      smoking_policy: form.smoking_policy || null,
      photos: photos.map((p) => ({ url: p.url })),
    };
    try {
      // iter106 — Guest-saved locations update via localStorage only.
      if (id && id.startsWith("g_")) {
        const updated = guestStore.updateCustomLocation(id, body);
        if (!updated) throw new Error("not_found");
      } else {
        await api.patch(`/custom-locations/${id}`, body);
      }
      toast.success("Location updated");
      navigate(`/my-places/custom/${id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <main className="min-h-screen" style={{ background: "#0B0B0B" }} />;
  }

  return (
    <main data-testid="custom-location-form" className="min-h-screen pb-32" style={{ background: "#0B0B0B" }}>
      <header
        className="px-5 pt-4 pb-3 flex items-center gap-3 sticky top-0 z-10"
        style={{ background: "rgba(11,11,11,0.92)", backdropFilter: "blur(8px)" }}
      >
        <button onClick={() => navigate(-1)} aria-label="Back" className="p-2">
          <ArrowLeft className="w-5 h-5 text-foreground/80" strokeWidth={1.5} />
        </button>
        <h1 className="flex-1 font-display text-xl text-foreground">
          {t("cl_form_edit_title")}
        </h1>
        <button
          type="button"
          onClick={() => navigate(`/my-places/custom/${id}`)}
          aria-label="Close"
          className="grid place-items-center rounded-full hover:bg-white/[0.05]"
          style={{ width: 32, height: 32, color: "#B0AAA3" }}
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </header>

      <div className="px-5 mt-3">
        <span className="inline-flex items-center gap-1 px-2.5 py-1 border text-[11px] uppercase tracking-widest text-gold" style={{ borderColor: "rgba(232,184,75,0.4)" }}>
          <Gem className="w-3 h-3" strokeWidth={1.5} /> Local Gem
        </span>
        <p className="text-[12px] text-foreground/55 mt-2">{t("cl_form_local_gem_note")}</p>
      </div>

      <Section title={t("cl_form_photos")}>
        <div className="grid grid-cols-3 gap-2">
          {photos.map((p, i) => (
            <div key={i} className="relative aspect-square overflow-hidden rounded-lg" style={{ background: "#171717" }}>
              <img src={p.url} alt={`photo-${i}`} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removePhoto(i)}
                data-testid={`cl-photo-remove-${i}`}
                aria-label="Remove photo"
                className="absolute top-1 right-1 w-7 h-7 grid place-items-center rounded-full"
                style={{ background: "rgba(0,0,0,0.7)", color: "#fff" }}
              >
                <X className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
            </div>
          ))}
          <label
            className="aspect-square flex flex-col items-center justify-center gap-1 text-gold cursor-pointer"
            style={{ background: "#171717", border: "1.5px dashed rgba(232,184,75,0.4)", borderRadius: 10 }}
          >
            <Camera className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-[11px]">{t("cl_form_add_photo")}</span>
            <input type="file" accept="image/*" multiple className="hidden" data-testid="cl-photo-input" onChange={onPhotoPick} />
          </label>
        </div>
      </Section>

      <Section title={t("cl_form_basics")}>
        <Field label={t("cl_form_name")} required>
          <Input data-testid="cl-name-input" value={form.name} onChange={set("name")} placeholder="Location name" />
        </Field>
        <Field label={t("cl_form_description")}>
          <textarea
            data-testid="cl-description-input"
            value={form.description} onChange={set("description")}
            placeholder="What makes this place special?"
            className="form-input"
            style={{ minHeight: 96, fontSize: 16, paddingTop: 10 }}
          />
        </Field>
      </Section>

      <Section title={t("cl_form_location")}>
        <Field label={t("cl_form_address")}>
          <Input data-testid="cl-address-input" value={form.address} onChange={set("address")} placeholder="Street, neighborhood" />
        </Field>
        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
          <Field label="Lat">
            <Input value={form.lat} onChange={set("lat")} placeholder="23.13" inputMode="decimal" />
          </Field>
          <Field label="Lng">
            <Input value={form.lng} onChange={set("lng")} placeholder="-82.38" inputMode="decimal" />
          </Field>
          <button
            type="button"
            data-testid="cl-gps-btn"
            onClick={captureGPS}
            className="flex items-center gap-1.5 text-gold border px-3 py-2.5 text-[13px]"
            style={{ borderColor: "rgba(232,184,75,0.4)", borderRadius: 8, minHeight: 48 }}
            aria-label="Capture GPS"
          >
            <MapPin className="w-4 h-4" strokeWidth={1.5} /> GPS
          </button>
        </div>
      </Section>

      <Section title={t("cl_form_contact")}>
        <Field label={`${t("cl_form_phone")}  ${optional(t)}`}>
          <Input data-testid="cl-phone-input" value={form.phone} onChange={set("phone")} placeholder="+53 5 555 5555" inputMode="tel" />
        </Field>
        <Field label={`${t("cl_form_whatsapp")}  ${optional(t)}`}>
          <Input data-testid="cl-whatsapp-input" value={form.whatsapp} onChange={set("whatsapp")} placeholder="+53 5 555 5555" inputMode="tel" />
        </Field>
        <Field label={`${t("cl_form_instagram")}  ${optional(t)}`}>
          <Input data-testid="cl-instagram-input" value={form.instagram} onChange={set("instagram")} placeholder="@handle" />
        </Field>
        <Field label={`${t("cl_form_website")}  ${optional(t)}`}>
          <Input data-testid="cl-website-input" value={form.website} onChange={set("website")} placeholder="https://" inputMode="url" />
        </Field>
      </Section>

      <Section title={t("cl_form_visiting")}>
        <Field label={`${t("cl_form_hours")}  ${optional(t)}`}>
          <Input data-testid="cl-hours-input" value={form.hours} onChange={set("hours")} placeholder="Mon-Fri 6pm-1am" />
        </Field>
        <Field label={`${t("cl_form_smoking_policy")}  ${optional(t)}`}>
          <select
            value={form.smoking_policy}
            onChange={set("smoking_policy")}
            data-testid="cl-smoking-input"
            className="form-input"
            style={{ minHeight: 48, fontSize: 16 }}
          >
            <option value="">—</option>
            <option value="allowed">Smoking allowed</option>
            <option value="not_allowed">Smoking not allowed</option>
            <option value="outdoor_only">Smoking — outdoor only</option>
          </select>
        </Field>
      </Section>

      <Section title={t("cl_form_my_notes")}>
        <p className="text-[12px] text-foreground/55 mb-2">{t("cl_form_my_notes_help")}</p>
        <textarea
          data-testid="cl-notes-input"
          value={form.notes} onChange={set("notes")}
          placeholder="Private notes — visible only to you"
          className="form-input"
          style={{ minHeight: 96, fontSize: 16, paddingTop: 10 }}
        />
      </Section>

      <div className="px-5 mt-9">
        <button
          type="button"
          data-testid="cl-save-btn"
          onClick={submit}
          disabled={busy || !form.name.trim()}
          className="w-full font-display text-base inline-flex items-center justify-center gap-2"
          style={{
            minHeight: 52,
            background: "#E8B84B",
            color: "#0B0B0B",
            border: "1px solid rgba(232,184,75,0.6)",
            borderRadius: 10,
            opacity: busy || !form.name.trim() ? 0.6 : 1,
          }}
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />}
          {t("cl_form_save_btn")}
        </button>
      </div>
    </main>
  );
}

function Section({ title, children }) {
  return (
    <section className="px-5 mt-7">
      <h2 className="eyebrow text-gold mb-3">{title}</h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="text-[12px] text-foreground/65 mb-1.5 block">
        {label}{required && <span className="text-gold ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}

function Input(props) {
  return (
    <input
      type="text"
      className="form-input"
      style={{ minHeight: 48, fontSize: 16 }}
      {...props}
    />
  );
}

function optional(t) {
  return `(${t("cl_form_optional") || "optional"})`;
}
